/**
 * Create-organization gate (P1). Shown inside /app when the signed-in user has
 * no membership yet. Creator becomes the org OWNER (→ app role manager,
 * R-PROD-01 spirit: the creator manages until reassigned).
 */
import { useState, type FormEvent } from 'react';
import { createOrg, setActiveOrg } from '../lib/authApi';
import { useSession } from '../lib/session';

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48) || 'org';
}

export function CreateOrg() {
  const { session, refresh, signOut } = useSession();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const { org, error: err } = await createOrg(name.trim(), slugify(name));
    if (!org) {
      setError(err ?? 'could not create the organization');
      setBusy(false);
      return;
    }
    await setActiveOrg(org.id);
    await refresh();
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div className="card" style={{ width: '100%', maxWidth: 440, padding: '30px 28px' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'calc(20px*var(--fs))', margin: '0 0 6px' }}>
          Create your organization
        </h2>
        <p className="hint" style={{ margin: '0 0 18px', lineHeight: 1.5 }}>
          Welcome, {session.userName || 'there'}. Products, SBOMs and Article 14 reports live inside an
          organization — its data is isolated from every other tenant. You'll be its first manager.
        </p>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="hint">Organization name</span>
            <input className="input" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. ACME Industrial AG" />
          </label>
          {name && <div className="hint mono">workspace id: {slugify(name)}</div>}
          {error && (
            <div className="hint" style={{ color: 'var(--danger, #c0392b)' }}>{error}</div>
          )}
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? '…' : 'Create organization →'}
          </button>
        </form>
        <div className="hint" style={{ marginTop: 16, textAlign: 'center' }}>
          Waiting on an invitation instead?{' '}
          <a style={{ color: 'var(--teal)', cursor: 'pointer' }} onClick={() => void signOut()}>Sign out</a>
        </div>
      </div>
    </div>
  );
}
